import { useCallback, useMemo } from 'react';
function collectDrafts(issues) {
    const result = [];
    for (const issue of issues) {
        if (issue.draftChanges) {
            for (const change of Object.values(issue.draftChanges)) {
                result.push(change);
            }
        }
        if (issue.children.length > 0) {
            result.push(...collectDrafts(issue.children));
        }
    }
    return result;
}
function buildDraftIndex(issues) {
    const index = new Map();
    const traverse = (items) => {
        for (const issue of items) {
            if (issue.draftChanges && Object.keys(issue.draftChanges).length > 0) {
                index.set(issue.issueKey, issue.draftChanges);
            }
            if (issue.children.length > 0) {
                traverse(issue.children);
            }
        }
    };
    traverse(issues);
    return index;
}
export function useDraftChanges(projectId, tree, refetchTree) {
    // Derive all drafts from tree data
    const allDrafts = useMemo(() => {
        if (!tree)
            return [];
        return [
            ...collectDrafts(tree.epics),
            ...collectDrafts(tree.unparentedIssues),
        ];
    }, [tree]);
    const draftCount = allDrafts.length;
    const pendingDraftCount = useMemo(() => allDrafts.filter(d => d.draftStatus !== 'submitted').length, [allDrafts]);
    const submittedDraftCount = useMemo(() => allDrafts.filter(d => d.draftStatus === 'submitted').length, [allDrafts]);
    // Build an index for fast hasDraft lookups
    const draftIndex = useMemo(() => {
        if (!tree)
            return new Map();
        return buildDraftIndex([...tree.epics, ...tree.unparentedIssues]);
    }, [tree]);
    // Group drafts by issue for the review panel
    const draftGroups = useMemo(() => {
        const groups = new Map();
        for (const draft of allDrafts) {
            if (!groups.has(draft.issueKey)) {
                groups.set(draft.issueKey, {
                    issueKey: draft.issueKey,
                    issueSummary: draft.issueSummary,
                    changes: [],
                });
            }
            groups.get(draft.issueKey).changes.push(draft);
        }
        return Array.from(groups.values());
    }, [allDrafts]);
    const hasDraft = useCallback((issueKey, fieldName) => {
        const fields = draftIndex.get(issueKey);
        return fields ? fieldName in fields : false;
    }, [draftIndex]);
    const getDraftStatus = useCallback((issueKey, fieldName) => {
        const fields = draftIndex.get(issueKey);
        if (!fields || !(fieldName in fields))
            return undefined;
        const draft = fields[fieldName];
        return draft?.draftStatus ?? 'pending';
    }, [draftIndex]);
    const addDraft = useCallback(async (issueKey, fieldName, oldValue, newValue) => {
        const response = await fetch(`/api/planning/${projectId}/drafts`, {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            credentials: 'include',
            body: JSON.stringify({ issueKey, fieldName, baseValue: oldValue, newValue }),
        });
        if (!response.ok) {
            const body = await response.json().catch(() => ({}));
            throw new Error(body.error || 'Failed to save draft');
        }
        await refetchTree();
    }, [projectId, refetchTree]);
    const bulkAddDrafts = useCallback(async (drafts) => {
        const response = await fetch(`/api/planning/${projectId}/drafts/bulk`, {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            credentials: 'include',
            body: JSON.stringify({
                drafts: drafts.map(d => ({
                    issueKey: d.issueKey,
                    fieldName: d.fieldName,
                    baseValue: d.oldValue ?? null,
                    newValue: d.newValue,
                })),
            }),
        });
        if (!response.ok) {
            const body = await response.json().catch(() => ({}));
            throw new Error(body.error || 'Failed to save bulk drafts');
        }
        await refetchTree();
    }, [projectId, refetchTree]);
    const removeDraft = useCallback(async (changeId) => {
        const response = await fetch(`/api/planning/${projectId}/drafts/${changeId}`, {
            method: 'DELETE',
            credentials: 'include',
        });
        if (!response.ok) {
            const body = await response.json().catch(() => ({}));
            throw new Error(body.error || 'Failed to remove draft');
        }
        await refetchTree();
    }, [projectId, refetchTree]);
    const discardAllDrafts = useCallback(async () => {
        const response = await fetch(`/api/planning/${projectId}/drafts`, {
            method: 'DELETE',
            credentials: 'include',
        });
        if (!response.ok) {
            const body = await response.json().catch(() => ({}));
            throw new Error(body.error || 'Failed to discard drafts');
        }
        await refetchTree();
    }, [projectId, refetchTree]);
    const checkConflicts = useCallback(async () => {
        const response = await fetch(`/api/planning/${projectId}/drafts/check-conflicts`, {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            credentials: 'include',
        });
        if (!response.ok) {
            const body = await response.json().catch(() => ({}));
            throw new Error(body.error || 'Failed to check conflicts');
        }
        const data = await response.json();
        return data.conflicts || [];
    }, [projectId]);
    const submitDrafts = useCallback(async (force) => {
        const response = await fetch(`/api/planning/${projectId}/drafts/submit`, {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            credentials: 'include',
            body: JSON.stringify({ force: force || false }),
        });
        if (!response.ok) {
            const body = await response.json().catch(() => ({}));
            throw new Error(body.error || 'Failed to submit changes');
        }
        const data = await response.json();
        if (data.success) {
            await refetchTree();
        }
        return data;
    }, [projectId, refetchTree]);
    return {
        draftCount,
        pendingDraftCount,
        submittedDraftCount,
        allDrafts,
        draftGroups,
        hasDraft,
        getDraftStatus,
        addDraft,
        bulkAddDrafts,
        removeDraft,
        discardAllDrafts,
        submitDrafts,
        checkConflicts,
    };
}
//# sourceMappingURL=useDraftChanges.js.map